// Словари и расчёты раздела «Задания процессов». Без React — чтобы их можно было
// проверить отдельно от отрисовки (tests/bp.test.tsx).
//
// Здесь же собраны формулировки, которые экран ОБЯЗАН показывать: данные бизнес-процессов
// читаются неверно без них. Главная — «просрочено» и «висит долго» это РАЗНЫЕ вещи:
// настоящий срок (OVERDUE_DATE) Bitrix проставил у 48 заданий из 6 989 (на 28.07.2026),
// а «висит дольше N дней» считаем мы сами. Слить их в одну красную цифру значило бы
// выдать нашу оценку за срок, назначенный процессом.

export const РАЗМЕР_СТРАНИЦЫ = 50

/** Тип задания. Ключи — значения ACTIVITY из bizproc.task.list, снятые с живого портала. */
export function видЗадания(activity: string | null | undefined): {
  ярлык: string
  смысл: string
  класс: string
} {
  switch (activity) {
    case 'ApproveActivity':
      return { ярлык: 'согласование', смысл: 'ждут «да» или «нет»', класс: 'badge work' }
    case 'ReviewActivity':
      return { ярлык: 'ознакомление', смысл: 'ждут отметки «прочитал»', класс: 'badge' }
    case 'RequestInformationActivity':
      return { ярлык: 'заполнить', смысл: 'ждут заполненной формы', класс: 'badge work' }
    case 'RequestInformationOptionalActivity':
      return { ярлык: 'заполнить (необяз.)', смысл: 'форму можно пропустить',
               класс: 'badge' }
    default:
      // Незнакомый тип показываем как есть: Bitrix волен завести новый, и прочерк на его
      // месте читался бы как «типа нет».
      return { ярлык: activity || 'тип не указан', смысл: 'тип задания неизвестен CRM',
               класс: 'badge' }
  }
}

/** Вид документа, по которому идёт процесс. */
export const ВИДЫ_ДОКУМЕНТА: Record<string, string> = {
  deal: 'Сделка',
  smart: 'Смарт-процесс',
  contact: 'Контакт',
  company: 'Компания',
  lead: 'Лид',
  list: 'Универсальный список',
  bp_list: 'Список бизнес-процессов',
  task: 'Задача',
}

export function названиеДокумента(вид: string | null | undefined,
                                  тип: number | null | undefined): string {
  const имя = ВИДЫ_ДОКУМЕНТА[вид || ''] || 'документ не разобран'
  return тип ? `${имя} ${тип}` : имя
}

/** Автозапуск шаблона. Значения AUTO_EXECUTE из bizproc.workflow.template.list. */
export const АВТОЗАПУСК: Record<number, string> = {
  0: 'вручную или из другого процесса',
  1: 'при добавлении',
  2: 'при изменении',
  3: 'при добавлении и изменении',
}

/**
 * Насколько долго висит задание. Пороги — не украшение: медиана живого задания на боевом
 * портале 74 дня, поэтому «неделя» ничего бы не выделяла. Возвращаем и класс, и словами,
 * чтобы на экране не оказалось голого числа без смысла.
 */
export function видОжидания(дней: number | null | undefined, порог: number): {
  ярлык: string
  класс: string
} {
  if (дней === null || дней === undefined) {
    return { ярлык: 'неизвестно', класс: 'muted' }
  }
  if (дней >= 365) return { ярлык: `${дней} дн. — больше года`, класс: 'badge lose' }
  if (дней >= порог) return { ярлык: `${дней} дн.`, класс: 'badge lose' }
  if (дней >= Math.round(порог / 2)) return { ярлык: `${дней} дн.`, класс: 'badge work' }
  return { ярлык: `${дней} дн.`, класс: '' }
}

/**
 * Почему задание нельзя закрыть из CRM. Отдельная функция, а не текст в разметке: причин
 * две, они разные, и путать их нельзя.
 *   • общая — CRM ходит служебным вебхуком, а Bitrix выполняет только СВОЁ задание;
 *   • частная — у задания обязательное файловое поле, и его не закрыть через REST вообще
 *     ничем, даже личным токеном сотрудника.
 */
export function почемуТолькоВBitrix(обязательныйФайл: boolean | null | undefined): string {
  return обязательныйФайл
    ? 'в задании обязательный файл — через REST такое не закрыть ничем, только в Bitrix'
    : 'закрыть задание может только сам исполнитель в Bitrix'
}

/** Ссылка в Bitrix на документ задания. Портал живёт на cp.cir.cx, путь приходит от него. */
export function ссылкаВBitrix(url: string | null | undefined): string | null {
  if (!url) return null
  if (/^https?:\/\//i.test(url)) return url
  return 'https://cp.cir.cx' + (url.startsWith('/') ? url : '/' + url)
}

/**
 * Что показать про сотрудника, которого уже нет. Это не украшение списка: заметная доля
 * живых заданий висит на отключённых учётках — их не закроет никто, потому что закрыть
 * задание может только сам исполнитель, и процесс за ними стоит навсегда. Такие строки
 * должны быть видны, а не тонуть среди прочих.
 *
 * Числа здесь нарочно нет: оно живое и растёт с каждым увольнением. Сколько сейчас —
 * говорит сам экран из `totals.left_tasks` / `left_people`, и оно посчитано на запрос.
 */
export function видСотрудника(active: boolean | null | undefined): {
  ярлык: string
  класс: string
} | null {
  if (active === false) return { ярлык: 'не работает', класс: 'badge lose' }
  if (active === null || active === undefined) {
    return { ярлык: 'нет в справочнике', класс: 'badge' }
  }
  return null
}
